import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  ActionRow,
  ModalDialog,
  Form,
  Alert,
  Badge,
  Spinner,
  Card,
} from '@openedx/paragon';
import { WarningFilled } from '@openedx/paragon/icons';
// eslint-disable-next-line import/no-unresolved
import { getUserProfile, removeEnterpriseLearner } from '../../../data/api';
import { fetchCompanyAdmins, refreshCompanyEmployees } from '../../../data/thunks';
import LearnerSearchDropdown, { LearnerOption } from './LearnerSearchDropdown';
import { logStudioHomeError } from '../../../logging';

interface RemoveLearnersModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCompany: any | null;
  learners: LearnerOption[];
  dispatch?: any;
  rosterIncomplete?: boolean;
}

interface FerpaVersion {
  version: string;
  acknowledged: boolean;
  acknowledged_timestamp: string;
  created: string;
}

interface EmployeeRecord {
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  name: string;
  company_name: string;
  friendly_name: string;
  platform_org: string | null;
  is_admin: boolean;
  is_primary_admin: boolean;
  ferpa_accepted: boolean;
  accepted_date: string | null;
  ferpa: FerpaVersion | null;
  job_title: string | null;
  metadata: any;
}

interface UserProfile {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  name: string;
  is_alumni: boolean;
  person_id: string | null;
  postal_code: string | null;
  company: string | null;
  pd_terms_accepted: boolean;
  bongo_terms_accepted: boolean;
  must_update_pass: boolean;
  employeeRecords: EmployeeRecord[];
  auth_provider: string;
  is_pd_admin: boolean;
}

const RemoveLearnersModal: React.FC<RemoveLearnersModalProps> = ({
  isOpen,
  onClose,
  selectedCompany,
  learners,
  dispatch: dispatchProp,
  rosterIncomplete = false,
}) => {
  // Use provided dispatch or get from hook
  const hookDispatch = useDispatch();
  const reduxDispatch = dispatchProp || hookDispatch;

  // Search state
  const [searchIdentifier, setSearchIdentifier] = useState('');
  const [learnerSearchValue, setLearnerSearchValue] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  // User profile state
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [submissionSuccess, setSubmissionSuccess] = useState('');

  // Handle search for user
  const handleSearch = async (identifier?: string) => {
    const id = identifier !== undefined ? identifier : searchIdentifier;
    if (!id.trim()) {
      setSearchError('Please select a learner from the dropdown');
      return;
    }

    if (!selectedCompany) {
      setSearchError('No organization selected');
      return;
    }

    setIsSearching(true);
    setSearchError('');
    setUserProfile(null);
    setShowConfirmation(false);
    setSubmissionError('');
    setSubmissionSuccess('');

    try {
      const profile = await getUserProfile(id.trim());

      // Check if user is in the selected organization by looking in employeeRecords
      const employeeRecord = profile.employeeRecords?.find(
        (emp: any) => emp.company_name === selectedCompany.company_name,
      );

      if (!employeeRecord) {
        setSearchError(
          `This user is not a member of ${selectedCompany.friendly_name} (${selectedCompany.company_name})`,
        );
        return;
      }

      setUserProfile(profile);
    } catch (error: any) {
      logStudioHomeError('cmp_learner_profile_fetch_failed', error);
      if (error.response?.status === 404) {
        setSearchError('User not found. Please check the email address or username.');
      } else {
        setSearchError('Failed to fetch user profile. Please try again.');
      }
    } finally {
      setIsSearching(false);
    }
  };

  // Handle learner selection from dropdown — auto-search
  const handleLearnerSelect = (learner: LearnerOption) => {
    setSearchIdentifier(learner.email);
    handleSearch(learner.email);
  };

  // Handle showing confirmation
  const handleShowConfirmation = () => {
    setShowConfirmation(true);
    setSubmissionError('');
  };

  // Handle canceling confirmation
  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  // Handle removing user from organization
  const handleRemoveFromOrg = async () => {
    if (!userProfile || !selectedCompany) {
      return;
    }

    setIsSubmitting(true);
    setSubmissionError('');
    setSubmissionSuccess('');

    try {
      // Call the new proxy API to remove the user from the organization
      await removeEnterpriseLearner(selectedCompany.company_name, userProfile.username);

      // Refetch companyEmployees to keep Redux state in sync
      if (reduxDispatch) {
        await reduxDispatch(refreshCompanyEmployees(selectedCompany.company_name));
        reduxDispatch(fetchCompanyAdmins());
      }

      setSubmissionSuccess(
        `Successfully removed ${userProfile.name} from ${selectedCompany.friendly_name}!`,
      );

      // Clear the user profile and confirmation state
      setUserProfile(null);
      setShowConfirmation(false);
      setSearchIdentifier('');
      setLearnerSearchValue('');
    } catch (error: any) {
      logStudioHomeError('cmp_learner_remove_failed', error);
      setSubmissionError('Failed to remove user from organization. Please try again.');
      setShowConfirmation(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle new search
  const handleNewSearch = () => {
    setSearchIdentifier('');
    setLearnerSearchValue('');
    setUserProfile(null);
    setShowConfirmation(false);
    setSearchError('');
    setSubmissionError('');
    setSubmissionSuccess('');
  };

  // Handle modal close
  const handleClose = () => {
    handleNewSearch();
    onClose();
  };

  // Get organization details for the user
  const userOrgDetails = userProfile && selectedCompany
    ? userProfile.employeeRecords?.find(emp => emp.company_name === selectedCompany.company_name)
    : null;

  return (
    <ModalDialog
      title="Remove Learners from Organization"
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      hasCloseButton
      isFullscreenOnMobile
    >
      <ModalDialog.Header>
        <ModalDialog.Title>Remove Learners from Organization</ModalDialog.Title>
      </ModalDialog.Header>

      <ModalDialog.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Success Message */}
        {submissionSuccess && (
          <Alert variant="success" dismissible onClose={() => setSubmissionSuccess('')}>
            {submissionSuccess}
          </Alert>
        )}

        {/* Search Form */}
        {!userProfile && !submissionSuccess && (
          <div className="mb-4">
            <h5 className="mb-3">Search for Learner</h5>

            {!selectedCompany && (
              <Alert variant="warning">
                Please select an organization first before removing learners.
              </Alert>
            )}

            {selectedCompany && (
              <>
                <Alert variant="info" className="mb-3">
                  <strong>Selected Organization:</strong> {selectedCompany.friendly_name}{' '}
                  ({selectedCompany.company_name})
                </Alert>

                <Form.Group>
                  <Form.Label>Learner</Form.Label>
                  <div style={{ marginBottom: isSearchFocused ? '14rem' : undefined }}>
                    <LearnerSearchDropdown
                      learners={learners}
                      value={learnerSearchValue}
                      onChange={setLearnerSearchValue}
                      onSelect={handleLearnerSelect}
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={() => setIsSearchFocused(false)}
                      placeholder="Search by name or email..."
                      disabled={isSearching}
                      id="remove-learner-search"
                      companyName={selectedCompany.company_name}
                      rosterIncomplete={rosterIncomplete}
                    />
                    {isSearching && (
                      <div className="mt-2 text-muted small d-flex align-items-center">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Loading learner profile...
                      </div>
                    )}
                    <Form.Text className="text-muted">
                      Search for a learner by name or email to remove them from this organization.
                    </Form.Text>
                  </div>
                </Form.Group>

                {searchError && (
                  <Alert variant="danger" className="mt-3">
                    {searchError}
                  </Alert>
                )}
              </>
            )}
          </div>
        )}

        {/* User Profile Display and Confirmation */}
        {userProfile && selectedCompany && !submissionSuccess && (
          <div>
            {submissionError && (
              <Alert variant="danger" dismissible onClose={() => setSubmissionError('')}>
                {submissionError}
              </Alert>
            )}

            {/* Button to search for another user */}
            <div className="mb-3">
              <Button variant="link" size="sm" onClick={handleNewSearch}>
                ← Search for another learner
              </Button>
            </div>

            {/* Warning Alert */}
            {!showConfirmation && (
              <Alert variant="warning" className="mb-4">
                <div className="d-flex align-items-start">
                  <WarningFilled className="me-2" style={{ fontSize: '1.5rem', flexShrink: 0 }} />
                  <div>
                    <strong>Warning:</strong> You are about to remove this learner from the organization.
                    This action will remove their access to all courses and programs associated with
                    this organization.
                  </div>
                </div>
              </Alert>
            )}

            {/* User Information Card */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Learner Information</h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <strong>Name:</strong> {userProfile.name}
                </div>
                <div className="mb-3">
                  <strong>Email:</strong> {userProfile.email}
                </div>
                <div className="mb-3">
                  <strong>Username:</strong> {userProfile.username}
                </div>
                {userOrgDetails && (
                  <>
                    <div className="mb-3">
                      <strong>Job Title:</strong> {userOrgDetails.job_title || 'N/A'}
                    </div>
                    <div className="mb-3">
                      <strong>Organization Role:</strong>{' '}
                      {userOrgDetails.is_admin ? (
                        <Badge variant="primary">Admin</Badge>
                      ) : (
                        <Badge variant="secondary">Learner</Badge>
                      )}
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>

            {/* Organization Removal Card */}
            <Card className="mb-4" style={{ borderColor: '#dc3545' }}>
              <Card.Header style={{ backgroundColor: '#dc354510' }}>
                <h5 className="mb-0 text-danger">Organization to Remove From</h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-2">
                  <strong>Organization Name:</strong> {selectedCompany.friendly_name}
                </div>
                <div>
                  <strong>Organization Code:</strong> {selectedCompany.company_name}
                </div>
              </Card.Body>
            </Card>

            {/* Final Confirmation */}
            {showConfirmation && (
              <Alert variant="danger" className="mb-4">
                <div className="d-flex align-items-start">
                  <WarningFilled className="me-2" style={{ fontSize: '1.5rem', flexShrink: 0 }} />
                  <div>
                    <strong>Final Confirmation:</strong>
                    <p className="mb-2 mt-2">
                      Are you absolutely sure you want to remove <strong>{userProfile.name}</strong> from{' '}
                      <strong>{selectedCompany.friendly_name}</strong>?
                    </p>
                    <p className="mb-0">
                      This will remove their B2B organization access and cannot be easily undone.
                    </p>
                  </div>
                </div>
              </Alert>
            )}
          </div>
        )}

        {/* Success state - option to remove another learner */}
        {submissionSuccess && (
          <div className="text-center py-4">
            <p>Would you like to remove another learner from this organization?</p>
            <Button variant="primary" onClick={handleNewSearch}>
              Remove Another Learner
            </Button>
          </div>
        )}
      </ModalDialog.Body>

      <ModalDialog.Footer>
        {!userProfile || submissionSuccess ? (
          <ActionRow>
            <ModalDialog.CloseButton variant="tertiary">
              Close
            </ModalDialog.CloseButton>
          </ActionRow>
        ) : (
          <ActionRow className="justify-content-between w-100">
            <ModalDialog.CloseButton variant="tertiary" disabled={isSubmitting}>
              Cancel
            </ModalDialog.CloseButton>
            <div className="d-flex">
              {showConfirmation ? (
                <>
                  <Button
                    variant="outline-secondary"
                    onClick={handleCancelConfirmation}
                    disabled={isSubmitting}
                    className="me-2"
                  >
                    Go Back
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleRemoveFromOrg}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Removing...
                      </>
                    ) : (
                      'Remove from Organization'
                    )}
                  </Button>
                </>
              ) : (
                <Button
                  variant="danger"
                  onClick={handleShowConfirmation}
                  disabled={isSubmitting}
                >
                  Continue to Remove
                </Button>
              )}
            </div>
          </ActionRow>
        )}
      </ModalDialog.Footer>
    </ModalDialog>
  );
};

export default RemoveLearnersModal;
